import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * The web twin of the app's CommonBottomDrawer / bottom-sheet Modals: a bottom
 * sheet on phones, a centred dialog on tablets and laptops. Rendered in a
 * portal (above the site header) inside its own `.brd` root so the theme
 * variables apply. Escape and a backdrop click close it — unless `busy`.
 */

// Body scroll is locked while any sheet is open (sheets can stack).
let openSheets = 0;
const lockScroll = () => {
  openSheets += 1;
  if (openSheets === 1) document.body.style.overflow = 'hidden';
};
const unlockScroll = () => {
  openSheets = Math.max(0, openSheets - 1);
  if (openSheets === 0) document.body.style.overflow = '';
};

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** While true nothing closes it (a request is in flight). */
  busy?: boolean;
  size?: 'small' | 'default' | 'wide' | 'full';
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Replaces the title row (a custom header, e.g. with a back arrow). */
  header?: React.ReactNode;
  /** Pinned under the scrolling body. */
  footer?: React.ReactNode;
  /** false hides the ✕ (e.g. a dialog with its own Cancel). */
  showClose?: boolean;
  /** Body without padding (the content lays itself out). */
  bare?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export function Sheet({ open, onClose, busy, size = 'default', title, subtitle, header, footer, showClose = true, bare, children, className }: SheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;
    lockScroll();
    const previous = document.activeElement as HTMLElement | null;
    // Focus the panel so Escape / screen readers start inside it.
    requestAnimationFrame(() => panelRef.current?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockScroll();
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = size === 'default' ? '' : ` brd-sheet--${size}`;
  return createPortal(
    <div
      className="brd brd-sheet-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`brd-sheet${sizeClass}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <div className="brd-sheet__handle" />
        {header ?? (title || showClose ? (
          <div className="brd-sheet__header">
            <div className="brd-sheet__heading">
              {!!title && <h2 className="brd-sheet__title" id={titleId}>{title}</h2>}
              {!!subtitle && <p className="brd-sheet__subtitle">{subtitle}</p>}
            </div>
            {showClose && (
              <button type="button" className="brd-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">
                <X size={20} />
              </button>
            )}
          </div>
        ) : null)}
        <div className="brd-sheet__body" style={bare ? { padding: 0 } : undefined}>{children}</div>
        {!!footer && <div className="brd-sheet__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export default Sheet;
